import { useEffect } from 'react';

// ── MODAL ────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 620 }) {
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(4,4,6,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
        backdropFilter: 'blur(6px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="fade-up"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border2)',
          borderRadius: 16,
          maxWidth: width, width: '100%',
          maxHeight: '92vh', overflowY: 'auto',
          boxShadow: '0 32px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(212,175,55,0.08)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '20px 28px', borderBottom: '1px solid var(--border)',
          position: 'sticky', top: 0, background: 'var(--surface)', zIndex: 1,
        }}>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 20, color: 'var(--gold)', margin: 0, fontWeight: 700,
          }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: 'var(--text-dim)',
              fontSize: 26, cursor: 'pointer', lineHeight: 1, padding: '0 4px',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-dim)'}
          >×</button>
        </div>
        <div style={{ padding: '24px 28px' }}>{children}</div>
      </div>
    </div>
  );
}

// ── STAR RATING ───────────────────────────────────────────────
export function StarRating({ value = 0, onChange, size = 22, readonly = false }) {
  const stars = [1,2,3,4,5,6,7,8,9,10];
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
      {stars.map(n => (
        <span
          key={n}
          onClick={() => !readonly && onChange && onChange(n)}
          style={{
            fontSize: size,
            cursor: readonly ? 'default' : 'pointer',
            color: n <= value ? 'var(--gold)' : 'var(--border2)',
            transition: 'color 0.1s, transform 0.1s',
            display: 'inline-block',
            userSelect: 'none',
          }}
          onMouseEnter={e => { if (!readonly) e.currentTarget.style.transform = 'scale(1.2)'; }}
          onMouseLeave={e => { if (!readonly) e.currentTarget.style.transform = 'scale(1)'; }}
        >★</span>
      ))}
    </div>
  );
}

// ── SPINNER ───────────────────────────────────────────────────
export function Spinner({ size = 32 }) {
  return (
    <div style={{
      width: size, height: size,
      border: `2px solid var(--border2)`,
      borderTop: `2px solid var(--gold)`,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
  );
}

// ── GOLD BUTTON ───────────────────────────────────────────────
export function GoldBtn({ children, onClick, disabled, outline, small, danger, style: extraStyle }) {
  const base = {
    fontFamily: "'Crimson Text', serif",
    fontSize: small ? 14 : 16,
    fontWeight: 600,
    padding: small ? '6px 14px' : '10px 22px',
    borderRadius: 6,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    letterSpacing: 0.3,
    opacity: disabled ? 0.5 : 1,
    ...extraStyle,
  };
  if (danger) return (
    <button onClick={!disabled ? onClick : undefined} style={{
      ...base, background: 'var(--red)', color: '#fff',
    }}>{children}</button>
  );
  if (outline) return (
    <button onClick={!disabled ? onClick : undefined} style={{
      ...base, background: 'transparent',
      border: '1px solid var(--gold-dim)', color: 'var(--gold)',
    }}>{children}</button>
  );
  return (
    <button onClick={!disabled ? onClick : undefined} style={{
      ...base, background: 'var(--gold)', color: 'var(--black)',
    }}>{children}</button>
  );
}

// ── CHIP TAG ──────────────────────────────────────────────────
export function Chip({ children, active, onClick }) {
  return (
    <span
      onClick={onClick}
      style={{
        display: 'inline-block',
        fontSize: 12, letterSpacing: 0.5,
        padding: '3px 9px', borderRadius: 4,
        border: `1px solid ${active ? 'var(--gold-dim)' : 'var(--border)'}`,
        background: active ? 'rgba(212,175,55,0.12)' : 'transparent',
        color: active ? 'var(--gold)' : 'var(--text-dim)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.15s',
        userSelect: 'none',
      }}
    >{children}</span>
  );
}

// ── INPUT ─────────────────────────────────────────────────────
export function Input({ label, value, onChange, type = 'text', placeholder, required }) {
  return (
    <div>
      {label && (
        <label style={{ display: 'block', fontSize: 13, color: 'var(--text-dim)', marginBottom: 6, letterSpacing: 0.5 }}>
          {label}{required && <span style={{ color: 'var(--gold)' }}> *</span>}
        </label>
      )}
      <input
        type={type} value={value} onChange={onChange} placeholder={placeholder}
        style={{
          width: '100%', background: 'var(--surface2)',
          border: '1px solid var(--border2)', borderRadius: 6,
          color: 'var(--text)', padding: '10px 14px', fontSize: 16,
          outline: 'none', transition: 'border-color 0.2s',
        }}
        onFocus={e => e.target.style.borderColor = 'var(--gold-dim)'}
        onBlur={e => e.target.style.borderColor = 'var(--border2)'}
      />
    </div>
  );
}

// ── SECTION DIVIDER ───────────────────────────────────────────
export function Divider({ label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      {label && <span style={{ color: 'var(--text-muted)', fontSize: 12, letterSpacing: 1, textTransform: 'uppercase' }}>{label}</span>}
      <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}
